import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { IconButton, Tooltip } from '@mui/material';
import { Reply as ReplyIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import PerfectScrollbar from 'react-perfect-scrollbar';

import Map from '../../components/Map/Map';
import Fader from '../../components/Loaders/Fader';
import FeedbackForm from '../../components/FeedbackForm/FeedbackForm';
import ReplyModal from '../../components/ReplyModal/ReplyModal';

import { getFeedback, setFeedbackStatus } from '../../../http/feedbackAPI';
import { createEmailBasedCase } from '../../../http/caseAPI';
import { setNotify } from '../../../datastore/actions/actionActions';
import { checkForEmptyString, formatNumberWithCommas, formatUnixDate } from '../../../util/HelperFunctions';
import { getRidingInfo } from '../../../http/ridingAPI';
import { getPollData, getPollInfo, getRidingData } from '../../../http/pollsAPI';
import { PARTIES_COLORS, PROVINCIAL_RESULTS, USER_ROLES } from '../../../util/constants/AppConstants';

import ChevronIcon from '../../../assets/images/icons/chevron.svg';
import CopyIcon from '../../../assets/images/icons/copy.svg';
import StarsIcon from '../../../assets/images/icons/stars.svg';
import './Message.scss';

const Message = () => {
  const dispatch = useDispatch();

  const config = useSelector((state) => state.config.config);

  const [message, setMessage] = useState(null);
  const [copiedIndex, setCopiedIndex] = useState(null);
  const [widgets, setWidgets] = useState([]);
  const [pollId, setPollId] = useState(false);
  const [pollData, setPollData] = useState(null);
  const [wardId, setWardId] = useState(null);
  const [ridingData, setRidingData] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isReplyModalOpen, setIsReplyModalOpen] = useState(false);
  const [statusUpdateLoading, setStatusUpdateLoading] = useState(false);
  const [caseCreateLoading, setCaseCreateLoading] = useState(false);

  const { userInfo, handleGetFeedbackData, setDeleteRequest, setDeleteId } = useOutletContext();

  const threadRef = useRef(null);

  const { id } = useParams();

  const navigate = useNavigate();

  const pollIdExists = pollId !== 'NaN';

  const mppRoleValue = USER_ROLES.find((role) => role.short === 'MPP').value;
  const councillorRoleValue = USER_ROLES.find((role) => role.short === 'Councillor').value;

  const isMpp = userInfo && userInfo.role === mppRoleValue;
  const isCouncillor = userInfo && userInfo.role === councillorRoleValue;

  useEffect(() => {
    if (threadRef.current) {
      const container = threadRef.current._container;

      if (container) {
        container.scrollTop = container.scrollHeight;
      }
    }
  }, [message?.responses]);

  const handleGetRidingInfo = async (mppId) => {
    const res = await getRidingInfo(config, mppId);

    if (res.status === 200) {
      setWidgets(res.data.riding.widgets.sort((a, b) => a.id - b.id));
    } else {
      console.log('ERROR: Grabbing Widgets');
    }
  };

  const handleGetPollData = async (pollId) => {
    const res = await getPollData(config, userInfo.id, pollId);

    if (res.status === 200) {
      setPollData(res.data.demographics ?? []);
    } else {
      console.log('ERROR: Grabbing Poll Data');
    }
  };

  const handleGetRidingData = async (ridingId) => {
    const res = await getRidingData(config, ridingId);

    if (res.status === 200) {
      setRidingData(res.data.riding);
    } else {
      console.log('ERROR: Grabbing Riding Data');
    }
  };

  const handleGetPollInfo = async (postalCode) => {
    const res = await getPollInfo(config, postalCode);

    if (res.status === 200) {
      if (isCouncillor) {
        if (!res.data.rows.length || !res.data.rows[0].ward_id) {
          setWardId('NaN');
          // handleGetCityData(userInfo.city);
          return;
        }

        setWardId(res.data.rows[0].ward_id);
        // handleGetCityData(userInfo.city);
      } else {
        if (!res.data.rows.length || !res.data.rows[0].poll_id) {
          setPollId('NaN');
          handleGetRidingData(userInfo.riding);
          return;
        }

        setPollId(res.data.rows[0].poll_id);
        handleGetPollData(res.data.rows[0].poll_id);
        handleGetRidingData(res.data.rows[0].riding_id);
      }
    } else {
      console.log('ERROR: Grabbing Poll information');
    }
  };

  const handleGetMessageInfo = async (id) => {
    const res = await getFeedback(config, userInfo.id, id);

    if (res.status === 200) {
      const { message } = res.data;

      if (message) {
        setMessage(message);
        if (isCouncillor) {
          // setWidgets([
          //   { id: 1, name: 'Location' },
          //   { id: 2, name: 'Demographic Snapshot' },
          // ]);
        } else {
          handleGetRidingInfo(message.mpp_id);
        }
        handleGetPollInfo(message.postal_code);
      }
    } else {
      console.log('ERROR: Grabbing Email Info');
    }
  };

  const handleUpdateFeedbackData = async () => {
    await handleGetMessageInfo(id);
    await handleGetFeedbackData(userInfo.id, userInfo.role);
  };

  useEffect(() => {
    if (config.api && userInfo && id) {
      handleGetMessageInfo(id);
    }
  }, [config, userInfo, id]);

  const handleCreateCase = async () => {
    setCaseCreateLoading(true);

    const res = await createEmailBasedCase(config, userInfo.id, id);

    if (res.status === 201) {
      dispatch(
        setNotify({
          status: true,
          type: 'success',
          title: 'Success',
          message: res.data.message,
        }),
      );

      await handleUpdateFeedbackData();
    } else {
      dispatch(
        setNotify({
          status: true,
          type: 'error',
          title: 'Error',
          message: res.data.message,
        }),
      );
    }

    setCaseCreateLoading(false);
  };

  const handleUpdateStatus = async (status, notify = true) => {
    setStatusUpdateLoading(true);

    const res = await setFeedbackStatus(config, userInfo.id, [id], status);

    if (res.status === 200) {
      if (notify) {
        dispatch(
          setNotify({
            status: true,
            type: 'success',
            title: 'Success',
            message: res.data.message,
          }),
        );
      }

      await handleUpdateFeedbackData();
    } else if (notify) {
      dispatch(
        setNotify({
          status: true,
          type: 'error',
          title: 'Error',
          message: res.data.message,
        }),
      );
    }
  };

  useEffect(() => {
    if ((isMpp || isCouncillor) && message && message.status === 'Unread') {
      handleUpdateStatus('Read', false);
    }
  }, [message]);

  const handleReplyMessage = () => {
    if (!message.email) {
      dispatch(
        setNotify({
          status: true,
          type: '',
          title: 'Attention',
          message: 'Cannot send reply: constituent has no email',
        }),
      );
    } else {
      setIsReplyModalOpen(true);
    }
  };

  const handleEditMessage = () => {
    setIsEditModalOpen(true);
  };

  const handleCloseEditForm = () => {
    setIsEditModalOpen(false);
  };

  const handleDeleteMessage = () => {
    setDeleteRequest(true);
    setDeleteId(id);
  };

  const handleCopy = (value, index) => {
    if (value !== '') {
      navigator.clipboard.writeText(value).then(() => {
        setCopiedIndex(index);
        setTimeout(() => setCopiedIndex(null), 1000);
      });
    }
  };

  if (!message) {
    return (
      <div className="wrap">
        <Fader />
      </div>
    );
  }

  return (
    <div className="message">
      <div className="message__header">
        <div className="message__back" onClick={() => navigate('/emails')}>
          <img src={ChevronIcon} alt="Back" />
          Back to Emails
        </div>
        {!message.readonly && (
          <div className="message__control">
            <div className="message__btns">
              <Tooltip title="Reply">
                <IconButton
                  className={`icon-button ${isMpp || isCouncillor ? 'blue' : ''}`}
                  onClick={handleReplyMessage}
                >
                  <ReplyIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Edit">
                <IconButton
                  className={`icon-button ${isMpp || isCouncillor ? 'blue' : ''}`}
                  onClick={handleEditMessage}
                >
                  <EditIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete">
                <IconButton
                  className={`icon-button ${isMpp || isCouncillor ? 'blue' : ''}`}
                  onClick={handleDeleteMessage}
                >
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            </div>
            <div className="message__btns">
              {message &&
                !message.case_id &&
                !message.readonly &&
                (caseCreateLoading ? (
                  <Fader />
                ) : (
                  <div className="table__btn create-btn secondary" onClick={() => handleCreateCase()}>
                    Create Case
                  </div>
                ))}
              {message &&
                message.status !== 'Handled' &&
                (statusUpdateLoading ? (
                  <Fader />
                ) : (
                  <div className="table__btn create-btn" onClick={() => handleUpdateStatus('Handled', true)}>
                    Mark As Handled
                  </div>
                ))}
            </div>
          </div>
        )}
      </div>
      <div className="message__info">
        <div className="message__status">
          <span className="message__date">{formatUnixDate(message.date_utc, true)}</span>
          {!message.readonly && <span className="message__category">{message.status}</span>}
        </div>
        <div className="message__wrap">
          <div className="message__block">
            <h2 className="message__title">{message.name || '(No name)'}</h2>
            <p className="message__text">{message.description ? message.description : '-'}</p>
          </div>
          <div className="message__block">
            <h2 className="message__title">Information</h2>
            <div className="message__information">
              <div className="message__line">
                <span>Address:</span>
                <span>{checkForEmptyString(message.address)}</span>
              </div>
              <div className="message__line">
                <span>Postal Code:</span>
                <span>{message.postal_code}</span>
              </div>
              <div className="message__line">
                <span>Email:</span>
                <span className="message__email-list">
                  {message.email
                    ? message.email.split(';').map((email, idx) => {
                        const trimmed = email.trim();
                        return (
                          <span
                            key={idx}
                            className={`message__email ${trimmed === '' ? 'empty' : ''}`}
                            onClick={() => handleCopy(trimmed, idx)}
                          >
                            {trimmed !== '' && (
                              <Tooltip title="Copy">
                                <img src={CopyIcon} alt="Copy" />
                              </Tooltip>
                            )}
                            {checkForEmptyString(trimmed)}
                            {copiedIndex === idx && <span className="message__notify">Email copied</span>}
                          </span>
                        );
                      })
                    : '-'}
                </span>
              </div>
              <div className="message__line">
                <span>Phone:</span>
                <span>{checkForEmptyString(message.phone)}</span>
              </div>
              <div className="message__line">
                <span>Contact Count:</span>
                <span>{message.contacts}</span>
              </div>
            </div>
            <h2 className="message__title">Category</h2>
            <span className="message__category">
              {message.type}
              {message.subtype && ` — ${message.subtype}`}
            </span>
            <div className="message__source-wrap">
              <div className="message__source">
                <h2 className="message__title">Source</h2>
                <div className="message__author">
                  {message.source ?? 'No data'}
                  {message.created_by_ai ? (
                    <div className="message__ai">
                      <img src={StarsIcon} />
                      AI
                    </div>
                  ) : (
                    ''
                  )}
                </div>
              </div>
              <div className="message__source">
                <h2 className="message__title">Added by</h2>
                <div className="message__author">{message.staffer_fullname}</div>
              </div>
              <div className="message__source">
                <h2 className="message__title">Case</h2>
                <div className="message__author">{message.case_id ?? 'Not created'}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {message.responses && message.responses.length > 0 && (
        <div className="message__demographics thread">
          <h2 className="message__title">Email Thread</h2>
          <div className="message__thread-wrapper">
            <PerfectScrollbar
              ref={threadRef}
              options={{
                wheelPropagation: false,
                autoHide: false,
              }}
            >
              <div className="message__thread">
                {message.responses.map((resp) => (
                  <div
                    key={resp.id}
                    className={`message__reply ${resp.author_type === 'user' ? 'outgoing' : 'incoming'}`}
                  >
                    <div className="message__reply-header">
                      <span className="message__reply-author">{resp.author}</span>
                      <div className="message__reply-header-right">
                        <span className="message__reply-date">{formatUnixDate(resp.date_utc, true)}</span>
                        {resp.author_type === 'constituent' && (
                          <Tooltip title="Reply">
                            <IconButton
                              className={`icon-button ${isMpp || isCouncillor ? 'blue' : ''}`}
                              onClick={handleReplyMessage}
                            >
                              <ReplyIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </div>
                    </div>
                    <div className="message__reply-text">{resp.message}</div>
                  </div>
                ))}
              </div>
            </PerfectScrollbar>
          </div>
        </div>
      )}
      {!isCouncillor && (
        <div className="message__demographics">
          <h2 className="message__title">Member notes</h2>
          <p className={`message__text ${message.member_notes !== '' ? 'notes' : ''}`}>
            {message.member_notes !== '' ? message.member_notes : '-'}
          </p>
        </div>
      )}
      {widgets.length > 0 && (
        <div className="message__demographics">
          <h2 className="message__title">Know Your Riding — Opportunity Scorecard</h2>
          {(pollIdExists && pollData && ridingData) || (!pollIdExists && ridingData) ? (
            <div className={`scorecard ${pollIdExists ? 'col3' : 'col2'}`}>
              {widgets.map((widget, index) => (
                <div className="scorecard__block" key={index}>
                  <div className="scorecard__header">
                    <h3 className="scorecard__title">
                      {index + 1}. {widget.name}
                      {widget.id === 4 && ' (Top 3)'}
                    </h3>
                  </div>
                  {widget.id === 1 && (
                    <div className="scorecard__info">
                      <div className="scorecard__line">
                        <span className="scorecard__text">Electoral Unit:</span>
                        {pollIdExists && <span className="scorecard__text">Poll {pollData.poll_label}</span>}
                        <span className="scorecard__text">{ridingData.riding}</span>
                      </div>
                    </div>
                  )}
                  {widget.id === 2 && (
                    <>
                      <div className="scorecard__info">
                        <div className="scorecard__line">
                          <span className="scorecard__text">Population:</span>
                          {pollIdExists && (
                            <span className="scorecard__text">{formatNumberWithCommas(pollData.total_population)}</span>
                          )}
                          <span className="scorecard__text">{formatNumberWithCommas(ridingData.total_population)}</span>
                        </div>
                        <div className="scorecard__line">
                          <span className="scorecard__text">Average age:</span>
                          {pollIdExists && (
                            <span className="scorecard__text">
                              Male - {pollData.avg_age_male}; Female - {pollData.avg_age_female}
                            </span>
                          )}
                          <span className="scorecard__text">
                            Male - {ridingData.avg_age_male}; Female - {ridingData.avg_age_female}
                          </span>
                        </div>
                        <div className="scorecard__line">
                          <span className="scorecard__text">Male / Female ratio:</span>
                          {pollIdExists && (
                            <div className="scorecard__column">
                              <div className="chart double">
                                <div className="chart__labels">
                                  <span className="chart__label">Male ({pollData.male_percentage}%)</span>
                                  <span className="chart__label">Female ({pollData.female_percentage}%)</span>
                                </div>
                                <div className="chart__line">
                                  <div
                                    className="chart__progress"
                                    style={{ width: `${pollData.male_percentage}%` }}
                                  ></div>
                                  <div
                                    className="chart__progress"
                                    style={{ width: `${pollData.female_percentage}%` }}
                                  ></div>
                                </div>
                              </div>
                            </div>
                          )}
                          <div className="scorecard__column">
                            <div className="chart double">
                              <div className="chart__labels">
                                <span className="chart__label">Male ({ridingData.male_percentage}%)</span>
                                <span className="chart__label">Female ({ridingData.female_percentage}%)</span>
                              </div>
                              <div className="chart__line">
                                <div
                                  className="chart__progress"
                                  style={{ width: `${ridingData.male_percentage}%` }}
                                ></div>
                                <div
                                  className="chart__progress"
                                  style={{ width: `${ridingData.female_percentage}%` }}
                                ></div>
                              </div>
                            </div>
                          </div>
                        </div>
                        {/* <div className="scorecard__line">
                          <span className="scorecard__text">Visible minority:</span>
                          <span className="scorecard__text">78%</span>
                        </div> */}
                        <div className="scorecard__line">
                          <span className="scorecard__text">Household size:</span>
                          {pollIdExists && (
                            <span className="scorecard__text">avg. {pollData.average_household_size} persons</span>
                          )}
                          <span className="scorecard__text">avg. {ridingData.average_household_size} persons</span>
                        </div>
                        {/* <div className="scorecard__line">
                          <span className="scorecard__text">Home ownership:</span>
                          <span className="scorecard__text">65% own; 35% rent</span>
                        </div> */}
                        {/* <div className="scorecard__line">
                          <span className="scorecard__text">Median income:</span>
                          <span className="scorecard__text">$82,000</span>
                        </div> */}
                        <div className="scorecard__line">
                          <span className="scorecard__text">Top occupations:</span>
                          {pollIdExists && (
                            <div className="scorecard__column">
                              <span className="scorecard__text">
                                {pollData.top_occupations.length > 0
                                  ? `1. ${pollData.top_occupations[0].label} (${pollData.top_occupations[0].percentage}%)`
                                  : '-'}
                              </span>
                              <span className="scorecard__text">
                                {pollData.top_occupations.length > 1
                                  ? `2. ${pollData.top_occupations[1].label} (${pollData.top_occupations[1].percentage}%)`
                                  : ''}
                              </span>
                            </div>
                          )}
                          <div className="scorecard__column">
                            <span className="scorecard__text">
                              {ridingData.top_occupations.length > 0
                                ? `1. ${ridingData.top_occupations[0].label} (${ridingData.top_occupations[0].percentage}%)`
                                : '-'}
                            </span>
                            <span className="scorecard__text">
                              {ridingData.top_occupations.length > 1
                                ? `2. ${ridingData.top_occupations[1].label} (${ridingData.top_occupations[1].percentage}%)`
                                : ''}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="scorecard__disclaimer">
                        * Data supplied by Esri and ArcGIS from November/December 2024
                      </div>
                    </>
                  )}
                  {widget.id === 3 && (
                    <>
                      <div className="scorecard__info">
                        <div className="scorecard__line">
                          <span className="scorecard__text">Party Affiliation:</span>
                          {pollIdExists && (
                            <div className="scorecard__column">
                              {pollData.party_results.map((party, i) => (
                                <div className="chart" key={i}>
                                  <span className="chart__label">
                                    {party.name} ({party.percentage}%)
                                  </span>
                                  <div className="chart__line">
                                    <div
                                      className="chart__progress"
                                      style={{
                                        width: `${party.percentage}%`,
                                        backgroundColor: `${PARTIES_COLORS.find((elem) => elem.id === party.id).color}`,
                                      }}
                                    ></div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                          <div className="scorecard__column">
                            {ridingData.party_results.map((party, i) => (
                              <div className="chart" key={i}>
                                <span className="chart__label">
                                  {party.name} ({party.percentage}%)
                                </span>
                                <div className="chart__line">
                                  <div
                                    className="chart__progress"
                                    style={{
                                      width: `${party.percentage}%`,
                                      backgroundColor: `${PARTIES_COLORS.find((elem) => elem.party === party.name).color}`,
                                    }}
                                  ></div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                        <div className="scorecard__line">
                          <span className="scorecard__text">Total votes:</span>
                          {pollIdExists && (
                            <span className="scorecard__text">{formatNumberWithCommas(pollData.votes)}</span>
                          )}
                          <span className="scorecard__text">{formatNumberWithCommas(ridingData.votes)}</span>
                        </div>
                      </div>
                      <div className="scorecard__disclaimer">* Data supplied by Elections Ontario from 2025</div>
                    </>
                  )}
                  {widget.id === 4 && (
                    <div className="scorecard__info">
                      <div className="scorecard__column">
                        {PROVINCIAL_RESULTS.map((party, i) => (
                          <div className="chart" key={i}>
                            <span className="chart__label">
                              {party.name} ({party.percentage}%)
                            </span>
                            <div className="chart__line">
                              <div
                                className="chart__progress"
                                style={{
                                  width: `${party.percentage}%`,
                                  backgroundColor: `${PARTIES_COLORS.find((elem) => elem.party === party.name).color}`,
                                }}
                              ></div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <Fader />
          )}
        </div>
      )}
      <div className="message__demographics">
        <h2 className="message__title">{isCouncillor ? 'Know Your City — Ward Map' : 'Know Your Riding — Poll Map'}</h2>
        {((!isCouncillor && pollId === 'NaN') || (isCouncillor && wardId === 'NaN')) && (
          <div className="poll-alert">
            Postal Code Data processing for specific {isCouncillor ? 'ward' : 'poll'}. Check back soon
          </div>
        )}
        <div className="map__box">
          {isCouncillor ? (
            wardId ? (
              <Map wardId={wardId} isCouncillor={true} />
            ) : (
              <Fader />
            )
          ) : pollId ? (
            <Map pollId={pollId} />
          ) : (
            <Fader />
          )}
        </div>
      </div>
      {isEditModalOpen && (
        <FeedbackForm
          isOpen={isEditModalOpen}
          onCloseHandler={handleCloseEditForm}
          feedbackData={message}
          handleGetFeedbackData={handleUpdateFeedbackData}
          userInfo={userInfo}
        />
      )}
      {isReplyModalOpen && (
        <ReplyModal
          isOpen={isReplyModalOpen}
          setIsOpen={setIsReplyModalOpen}
          caseId={message.case_id}
          recipientEmail={message.email.split(';')[0]}
          handleGetFeedbackData={() => handleGetMessageInfo(id)}
        />
      )}
    </div>
  );
};

export default Message;
